import React, { useState } from 'react';
import { confirmSignUp, resendSignUpCode, signIn, sendUserAttributeVerificationCode, confirmUserAttribute } from 'aws-amplify/auth';

type VerificationPath = 'email' | 'phone' | 'both';

interface VerificationFormProps {
  identifier: string;       // email or phone number (E.164) used as Cognito username
  verificationPath: VerificationPath;
  phone?: string;           // phone in E.164 — needed for 'both' path
  password?: string;        // needed for auto-login before phone verification on 'both' path
  onSuccess?: () => void;
  onBack?: () => void;
}

const VerificationForm: React.FC<VerificationFormProps> = ({
  identifier,
  verificationPath,
  phone,
  password,
  onSuccess,
  onBack,
}) => {
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'phone'>(verificationPath === 'phone' ? 'phone' : 'email');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const isPhoneStep = step === 'phone';
  const destination = isPhoneStep ? (phone ?? identifier) : identifier;
  const destinationLabel = isPhoneStep ? `SMS to ${destination}` : destination;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (isPhoneStep) {
        await confirmUserAttribute({ userAttributeKey: 'phone_number', confirmationCode: code });
        setMessage('Phone number verified!');
        setTimeout(() => onSuccess?.(), 1500);
      } else {
        await confirmSignUp({ username: identifier, confirmationCode: code });

        if (verificationPath === 'both' && phone && password) {
          // Auto-sign-in so we can trigger phone attribute verification
          await signIn({ username: identifier, password });
          await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
          setCode('');
          setStep('phone');
          setMessage('Email verified! Now enter the code sent via SMS to your phone.');
        } else {
          setMessage('Account verified! You can now sign in.');
          setTimeout(() => onSuccess?.(), 1500);
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setError('');
    setMessage('');
    try {
      if (isPhoneStep) {
        await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
        setMessage('Code resent via SMS.');
      } else {
        await resendSignUpCode({ username: identifier });
        setMessage(`Code resent. Check your ${verificationPath === 'phone' ? 'phone' : 'email'}.`);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to resend code.');
    } finally {
      setIsResending(false);
    }
  };

  const stepLabel = verificationPath === 'both'
    ? step === 'email' ? 'Step 1 of 2 — Email' : 'Step 2 of 2 — Phone'
    : null;

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">Verify Your Account</h2>

      {stepLabel && (
        <p className="text-center text-xs font-medium text-primary-600 mb-4">{stepLabel}</p>
      )}

      <p className="text-center text-gray-600 mb-4 text-sm">
        We sent a verification code to <strong>{destinationLabel}</strong>.
        Enter it below to complete your registration.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">{error}</div>
      )}
      {message && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm">{message}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="code" className="block text-sm font-medium text-gray-700">Verification Code</label>
          <input
            id="code" type="text" value={code} onChange={e => setCode(e.target.value)}
            required maxLength={6}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-center text-lg tracking-widest"
            placeholder="123456"
          />
        </div>

        <button
          type="submit" disabled={isLoading || !code}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Verifying…' : 'Verify'}
        </button>
      </form>

      <div className="mt-6 text-center space-y-2">
        <button
          onClick={handleResend} disabled={isResending}
          className="text-sm text-primary-600 hover:text-primary-500 disabled:opacity-50"
        >
          {isResending ? 'Sending…' : 'Resend code'}
        </button>

        {!isPhoneStep && (
          <div>
            <button onClick={onBack} className="text-sm text-gray-600 hover:text-gray-500">
              ← Back to sign up
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationForm;
